// AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUserById } from '../../crud/users_api/users';
import { getClientById } from '../../crud/clients_api/clients';
import { loadContext } from '../common/ContextLoader/ContextLoader';
import localDB from '../../crud/localDB/localDB';

interface AuthContextType {
  loggedIn: boolean;
  loading: boolean;
  forceLogout: () => Promise<void>;
  setUserData: (userData: UserData) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

interface UserData {
  user_id: number;
  state: number;
  [key: string]: any;
}

interface ClientData {
  client_id: number;
  [key: string]: any;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Force logout function that clears all auth state
  const forceLogout = async (): Promise<void> => {
    try {
      // Clear persistent auth state in localDB
      await localDB.set('auth', 'isLogged', false);
      await localDB.set('auth', 'loggedOut', true);
      await localDB.delete('auth', 'lastCheck');
      await localDB.delete('auth', 'lastValidation');
      
      // Clear localStorage user/client data
      localStorage.removeItem('user');
      localStorage.removeItem('client');
      localStorage.setItem('loggedOut', 'true');
      
      // Clear auth tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Clear context data loaded by ContextLoader
      localStorage.removeItem('local');
      localStorage.removeItem('localConfiguration');
      localStorage.removeItem('userPermissions');
      
      // Update state
      setLoggedIn(false);
    } catch (error) {
      console.error('Error during force logout:', error);
      // Fallback to localStorage if localDB fails
      localStorage.removeItem('user');
      localStorage.removeItem('client');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('local');
      localStorage.removeItem('localConfiguration');
      localStorage.removeItem('userPermissions');
      localStorage.setItem('loggedOut', 'true');
      setLoggedIn(false);
    }
  };

  // Set user data function for login components
  const setUserData = async (userData: UserData): Promise<void> => {
    try {
      // Validate user security before setting data
      if (!validateUserSecurity(userData)) {
        console.warn('Cannot set user data: user security validation failed');
        await forceLogout();
        return;
      }

      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Load context immediately after setting user data
      await loadContext(() => {}, (error) => {
        if (error) {
          console.error('Error loading context after login:', error);
        }
      });
      
      // Update persistent auth state
      const now = Date.now();
      await localDB.set('auth', 'isLogged', true);
      await localDB.set('auth', 'lastCheck', now);
      await localDB.set('auth', 'lastValidation', { userId: userData.user_id, state: userData.state });
      await localDB.delete('auth', 'loggedOut');
      localStorage.removeItem('loggedOut');
      
      // Update state
      setLoggedIn(true);
      
      // Notify other components that user data has been updated
      console.log('Dispatching userDataUpdated event...');
      window.dispatchEvent(new Event('userDataUpdated'));
    } catch (error) {
      console.error('Error setting user data:', error);
      // Fallback: still store in localStorage but warn about errors
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('loggedOut');
      setLoggedIn(true);
      
      // Still notify components even if localDB failed
      window.dispatchEvent(new Event('userDataUpdated'));
    }
  };

  // Validate user state and handle security checks
  const validateUserSecurity = (userData: UserData): boolean => {
    // Check if user account is disabled (state = 0)
    if (userData.state === 0) {
      console.warn('User account is disabled, forcing logout');
      return false;
    }
    
    // Add additional security checks here as needed
    // e.g., check for suspended accounts, expired passwords, etc.
    
    return true;
  };

  useEffect(() => {
    const validateAuth = async () => {
      try {
        const now = Date.now();
        
        // Check if user explicitly logged out
        const loggedOut = await localDB.get('auth', 'loggedOut');
        if (loggedOut) {
          setLoggedIn(false);
          setLoading(false);
          return;
        }

        // Check for cached validation (since we now have API caching)
        const lastCheck = await localDB.get('auth', 'lastCheck');
        const cached = await localDB.get('auth', 'isLogged');
        const lastValidation = await localDB.get('auth', 'lastValidation');

        // Reuse cache if less than 30 minutes old (reduced from 1 hour for security)
        const cacheValidityMs = 30 * 60 * 1000; // 30 minutes
        if (lastCheck && cached !== null && lastValidation && 
            now - Number(lastCheck) < cacheValidityMs) {
          setLoggedIn(cached === true);
          setLoading(false);
          return;
        }

        // Get user/client data from localStorage
        const userJson = localStorage.getItem('user');
        const clientJson = localStorage.getItem('client');

        const finishAuth = async (isLogged: boolean, validationData?: any) => {
          try {
            await localDB.set('auth', 'isLogged', isLogged);
            await localDB.set('auth', 'lastCheck', now);
            if (validationData) {
              await localDB.set('auth', 'lastValidation', validationData);
            }
            // Clear explicit logout flag if login successful
            if (isLogged) {
              await localDB.delete('auth', 'loggedOut');
              localStorage.removeItem('loggedOut');
            }
          } catch (error) {
            console.error('Error saving auth state to localDB:', error);
          }
          
          setLoggedIn(isLogged);
          setLoading(false);
        };

        // Validate user authentication
        if (userJson && !clientJson) {
          const userObj = JSON.parse(userJson);
          
          try {
            const response = await getUserById(userObj.user_id);
            
            if (response.status === 200) {
              const userData: UserData = response.data;
              
              // Security validation: check if user is disabled
              if (!validateUserSecurity(userData)) {
                console.warn('User security validation failed, logging out');
                await forceLogout();
                window.location.href = '/login';
                return;
              }
              
              // Merge API response with existing data to preserve fields like is_admin
              // that might only come from the login response
              const mergedUserData = {
                ...userObj,      // Keep existing fields (like is_admin from login)
                ...userData      // Override with fresh data from API
              };
              
              // Always update localStorage with merged user data
              localStorage.setItem('user', JSON.stringify(mergedUserData));
              
              await finishAuth(true, { userId: userData.user_id, state: userData.state });
            } else {
              // API returned non-200, user might not exist or be unauthorized
              await finishAuth(false);
            }
          } catch (error) {
            console.error('Error validating user:', error);
            await finishAuth(false);
          }
        } 
        // Validate client authentication  
        else if (clientJson) {
          const clientObj = JSON.parse(clientJson);
          
          try {
            const response = await getClientById(clientObj.client_id);
            await finishAuth(response.status === 200, { clientId: clientObj.client_id });
          } catch (error) {
            console.error('Error validating client:', error);
            await finishAuth(false);
          }
        } 
        // No authentication data
        else {
          await finishAuth(false);
        }
        
      } catch (error) {
        console.error('Auth validation error:', error);
        setLoggedIn(false);
        setLoading(false);
      }
    };

    validateAuth();
  }, []);

  // Periodic security check for active sessions
  useEffect(() => {
    if (!loggedIn) return;

    const securityCheckInterval = setInterval(async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const userObj = JSON.parse(userJson);
          const response = await getUserById(userObj.user_id);
          
          if (response.status === 200) {
            const userData: UserData = response.data;
            
            // Check if user was disabled during the session
            if (!validateUserSecurity(userData)) {
              console.warn('User was disabled during session, forcing logout');
              await forceLogout();
              window.location.href = '/login';
              return;
            }
            
            // Merge with existing data to preserve fields like is_admin
            const mergedUserData = {
              ...userObj,
              ...userData
            };
            
            // Update localStorage with merged data
            localStorage.setItem('user', JSON.stringify(mergedUserData));
          } else {
            // User no longer exists or API error
            console.warn('User validation failed during session, forcing logout');
            await forceLogout();
            window.location.href = '/login';
          }
        }
      } catch (error) {
        console.error('Periodic security check failed:', error);
        // Don't force logout on network errors, but log the issue
      }
    }, 10 * 60 * 1000); // Check every 10 minutes

    return () => clearInterval(securityCheckInterval);
  }, [loggedIn]);

  return (
    <AuthContext.Provider value={{ loggedIn, loading, forceLogout, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
